import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import UserService from "services/User";
import RegionService from "services/Regions";
import {
  getCampaignsTableColumns,
  getCampaignsTableFilters,
} from "routes/campaign/CampaignsTableColumnsAndFilters";
import CampaignService from "services/Campaign";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import { CAMPAIGNS_TABLE_CONSTANTS } from "routes/campaign/CampaignsTableV3";

export const invalidateCampaignsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [CAMPAIGNS_TABLE_CONSTANTS.queryKey],
  });
};

export const deleteCampaign = async id => {
  await CampaignService.deleteCampaign(id);
  await invalidateCampaignsQuery();
};

const handleFetchRegions = async () => {
  const rawRegions = await RegionService.getRegions();

  const regions = rawRegions.map(({ id, name, countries = [] }) => ({
    id,
    name,
    extraData: { countries },
  }));

  return regions;
};

const handleFetchCustomFields = async () => {
  const customFields = await CampaignService.getCampaignCustomFields();
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel("Campaign");
  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Campaign",
      fields: customFields,
      model: "Campaign",
      configs,
    },
  ];
};

export const useCampaignTable = () => {
  const { data: regions, isSuccess: isRegionsFetched } = useQuery({
    queryKey: ["regions", CAMPAIGNS_TABLE_CONSTANTS.className],
    queryFn: handleFetchRegions,
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", CAMPAIGNS_TABLE_CONSTANTS.className],
    queryFn: handleFetchCustomFields,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isRegionsFetched || !isCustomFieldsFetched) {
      return [];
    }
    const finalFilters = getCampaignsTableFilters({
      regions: regions,
    });

    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }

    return finalFilters;
  }, [regions, customFields, isRegionsFetched, isCustomFieldsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getCampaignsTableColumns();
    const campaignsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CAMPAIGNS_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: campaignsTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: campaignsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    isTableSettingsFetched,
    isCustomFieldsFetched,
  ]);

  return {
    filters,
    columns,
    customFields,
  };
};
